import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, Unlock } from "lucide-react";
import { formatApiError } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function MonthLocks({ companyContext }) {
  const { companyId } = companyContext || {};
  const [locks, setLocks] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/month-locks`, { params: { company_id: companyId } });
      setLocks(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (companyId) load(); /* eslint-disable-next-line */ }, [companyId]);

  const lockedSet = useMemo(() => new Set(locks.map((l) => `${l.year}-${l.month}`)), [locks]);
  const locksByKey = useMemo(() => {
    const m = {};
    for (const l of locks) m[`${l.year}-${l.month}`] = l;
    return m;
  }, [locks]);

  const toggle = async (yr, mo) => {
    const key = `${yr}-${mo}`;
    try {
      if (lockedSet.has(key)) {
        await axios.delete(`${API}/month-locks`, { params: { company_id: companyId, year: yr, month: mo } });
        toast.success(`${MONTHS[mo - 1]} ${yr} unlocked`);
      } else {
        await axios.post(`${API}/month-locks`, { company_id: companyId, year: yr, month: mo, note });
        toast.success(`${MONTHS[mo - 1]} ${yr} locked`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="month-locks-page">
      <div>
        <h1 className="text-3xl font-[Chivo] font-semibold tracking-tight">Month Lock</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Lock a month to freeze the books. Locked months cannot receive new uploads or closing-stock edits —
          Audit JVs are the only way to post corrections.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle className="text-base">Calendar</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setYear(year - 1)} data-testid="ml-year-prev">←</Button>
              <Input
                className="w-24 text-center tabular-nums"
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value || "0", 10))}
                data-testid="ml-year-input"
              />
              <Button variant="outline" size="sm" onClick={() => setYear(year + 1)} data-testid="ml-year-next">→</Button>
            </div>
          </div>
          <div className="pt-3">
            <Input
              placeholder="Optional note — e.g. 'Audit signed off on 05-May'"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="max-w-lg"
              data-testid="ml-note-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {MONTHS.map((name, i) => {
              const mo = i + 1;
              const key = `${year}-${mo}`;
              const locked = lockedSet.has(key);
              const meta = locksByKey[key];
              return (
                <button
                  key={mo}
                  onClick={() => toggle(year, mo)}
                  data-testid={`ml-toggle-${year}-${mo}`}
                  className={`border rounded-lg p-3 text-left transition ${
                    locked
                      ? "bg-zinc-950 text-white border-zinc-900 hover:opacity-90"
                      : "bg-white text-zinc-900 border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`text-[10px] uppercase tracking-wider ${locked ? "text-zinc-500" : "text-zinc-500"}`}>
                        {name}
                      </div>
                      <div className="text-lg font-semibold font-[IBM_Plex_Mono] tabular-nums">{year}</div>
                    </div>
                    {locked ? <Lock className="h-4 w-4 text-amber-400" /> : <Unlock className="h-4 w-4 text-zinc-400" />}
                  </div>
                  {locked && meta?.note && (
                    <div className="mt-2 text-[11px] text-zinc-400 line-clamp-2">{meta.note}</div>
                  )}
                  {locked && meta?.locked_by_email && (
                    <div className="mt-1 text-[10px] text-zinc-500 truncate">by {meta.locked_by_email}</div>
                  )}
                </button>
              );
            })}
          </div>
          {loading && <p className="text-xs text-zinc-500 mt-4">Loading…</p>}
        </CardContent>
      </Card>
    </div>
  );
}
